import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { allTutorialSteps } from '../utils/tutorialSteps';

const TutorialContext = createContext();

export function TutorialProvider({ children }) {
  const [joyrideState, setJoyrideState] = useState({
    run: false,
    steps: [],
    stepIndex: 0,
    tutorialKey: '',
  });

  const startTutorial = useCallback((tutorialName, runWithBeacon = false) => {
    const steps = allTutorialSteps[tutorialName];
    if (!steps) {
      console.error(`Tutorial "${tutorialName}" not found.`);
      return;
    }

    const tutorialKey = `tutorial_${tutorialName}_completed`;
    const hasCompleted = localStorage.getItem(tutorialKey);

    if (runWithBeacon && hasCompleted) {
      return;
    }

    setJoyrideState({
      run: true,
      steps: steps,
      stepIndex: 0,
      tutorialKey: tutorialKey,
      continuous: true,
      showProgress: true,
      showSkipButton: true,
      disableScrolling: false, 
      disableScrollParentFix: false, 
      placement: 'auto', 
      disableOverlayClose: true,
      scrollOffset: 200,
      floaterProps: {
        disableFlip: false,
        wrapperOptions: {
          attributes: {
            'data-joyride-floater': true, 
          }
        }
      },
      
      // German Translation Configuration
      locale: { 
        back: 'Zurück',
        close: 'Schließen',
        last: 'Beenden',
        next: 'Weiter',
        open: 'Öffnen',
        skip: 'Überspringen',
      },
      
      styles: {
        options: {
          arrowColor: 'var(--component-bg)',
          backgroundColor: 'var(--component-bg)',
          primaryColor: 'var(--link-color)', 
          textColor: 'var(--text-color)',
          zIndex: 10000, 
        },
        tooltip: {
          border: `1px solid var(--border-color)`,
          borderRadius: '8px',
          boxShadow: `0 4px 12px var(--modal-shadow)`,
          color: 'var(--text-color)',
        },
        tooltipHeader: {
          borderBottom: `1px solid var(--border-color-light)`,
          padding: '1rem',
          margin: 0,
          color: 'var(--text-color)',
          fontSize: '1rem', 
        },
        tooltipContent: {
          padding: '1rem',
          fontSize: '0.9rem', 
        },
        tooltipFooter: {
          padding: '0.5rem 1rem 1rem',
          margin: 0,
        },
        buttonSkip: {
          color: 'var(--text-color-light)',
          fontSize: '0.9rem',
        },
        buttonBack: {
          color: 'var(--link-color)',
          marginRight: 'auto',
        },
        buttonClose: {
          display: 'none', 
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        },
      },

      type: runWithBeacon ? 'beacon' : 'tooltip',
    });
  }, []);

  const handleJoyrideCallback = useCallback((data) => {
    const { action, index, status, type, step } = data;
    const finishedStatuses = ['finished', 'skipped'];

    if (finishedStatuses.includes(status)) {
      setJoyrideState(prevState => {
        if (prevState.tutorialKey) {
          localStorage.setItem(prevState.tutorialKey, 'true');
        }
        return {
          ...prevState,
          run: false,
          stepIndex: 0
        };
      });

    } else if (type === 'step:after') {
      
      if (step.target === '[data-tut="profile-user-box"]' && action === 'next') {
        return; 
      }
      
      if (action === 'next' || action === 'prev') {
        setJoyrideState(prevState => {
          const newIndex = index + (action === 'prev' ? -1 : 1);
          return {
            ...prevState,
            stepIndex: newIndex
          };
        });
      }

    } else if (action === 'close' && type === 'overlay') {
      // Translated
      alert('Bitte schließen oder überspringen Sie das Tutorial, bevor Sie mit der Seite interagieren.');

      const tooltip = document.querySelector('[data-joyride-floater="true"]');
      if (tooltip) {
        tooltip.classList.add('joyride-shake');
        setTimeout(() => {
          tooltip.classList.remove('joyride-shake');
        }, 500); 
      }

    } else if (type === 'error:target_not_found') {
        console.warn(`Joyride target not found: ${step?.target}`);
        setJoyrideState(prevState => {
          const stepDelta = action === 'prev' ? -1 : 1;
          const nextIndex = Math.max(0, prevState.stepIndex + stepDelta);

          if (nextIndex >= prevState.steps.length) {
            if (prevState.tutorialKey) {
              localStorage.setItem(prevState.tutorialKey, 'true');
            }
            return {
              ...prevState,
              run: false,
              stepIndex: 0,
            };
          }

          return {
            ...prevState,
            stepIndex: nextIndex,
          };
        });
    }
  }, []);

  const goToNextStep = useCallback(() => {
    setJoyrideState(prevState => ({
      ...prevState,
      stepIndex: prevState.stepIndex + 1,
    }));
  }, []);

  const value = useMemo(() => ({
    joyrideState,
    startTutorial,
    handleJoyrideCallback,
    goToNextStep,
  }), [joyrideState, startTutorial, handleJoyrideCallback, goToNextStep]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
}

export const useTutorial = () => {
  return useContext(TutorialContext);
};